import { useMemo, useState } from "react";
import { HiInboxStack, HiSparkles } from "react-icons/hi2";
import { SignalRow } from "./SignalRow";
import { HubspotSyncIndicator } from "./HubspotSyncIndicator";
import {
  SIGNAL_STATUSES,
  STATUS_ICONS,
  STATUS_LABELS,
} from "./signalLifecycle";
import { useEmptyStateTracking } from "../../../hooks/useEmptyStateTracking";

const EMPTY_COPY = {
  active: "No active signals for this account.",
  resolved: "No resolved signals yet.",
  snoozed: "No snoozed signals.",
  rejected: "No rejected signals.",
};

const EmptyState = ({ status }) => (
  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
    <HiInboxStack className="mx-auto mb-2 size-6 text-gray-300" />
    <p className="text-sm text-gray-400">
      {EMPTY_COPY[status] ?? "No signals."}
    </p>
  </div>
);

/**
 * Signals section — lifecycle-aware queue with status tabs.
 *
 * @param {Object[]} signals           - All transformed signals (any status)
 * @param {Object}   [hubspotSync]      - {status, last_synced_at, signal_count} from the
 *                                        account payload, or null when the team does not
 *                                        have the HubSpot signal writeback (PAN-677)
 * @param {Function} onUpdateActionItem - (alertId, index, {status?, note?}) => Promise
 * @param {Function} onResolve          - (alertId) => Promise
 * @param {Function} onSnooze           - (alertId, snoozedUntilISO) => Promise
 * @param {Function} onReject           - (alertId, reason, note?) => Promise
 * @param {Function} onReactivate       - (alertId) => Promise (revert a terminal signal)
 * @param {Function} [onSignalViewed]   - (signal) => void (analytics on expand)
 */
export const SignalsSection = ({
  signals: initialSignals,
  hubspotSync,
  onUpdateActionItem,
  onResolve,
  onSnooze,
  onReject,
  onReactivate,
  onSignalViewed,
}) => {
  // Local overrides for optimistic action-item status/notes.
  const [localOverrides, setLocalOverrides] = useState({});
  const [activeStatus, setActiveStatus] = useState("active");
  const [expandedId, setExpandedId] = useState(null);

  const signals = (initialSignals ?? []).map((sig) => {
    const overrides = localOverrides[sig.id];
    if (!overrides) return sig;
    return {
      ...sig,
      actions: sig.actions.map((a) => ({
        ...a,
        status: overrides.actionStatuses?.[a.id] ?? a.status,
        note: overrides.actionNotes?.[a.id] ?? a.note,
      })),
    };
  });

  // Group by lifecycle status for tab counts + filtering.
  const grouped = useMemo(() => {
    const buckets = { active: [], resolved: [], snoozed: [], rejected: [] };
    signals.forEach((sig) => {
      (buckets[sig.status] ?? buckets.active).push(sig);
    });
    return buckets;
  }, [signals]);

  const activeCount = grouped.active.length;

  useEmptyStateTracking("signals_list", {
    isEmpty: activeCount === 0,
    isLoading: false,
  });

  const handleToggle = (id) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id;
      if (next === id && onSignalViewed) {
        const sig = signals.find((s) => s.id === id);
        if (sig) onSignalViewed(sig);
      }
      return next;
    });
  };

  /** Parse "{alertId}-act-{index}" → [alertId, index]. */
  const parseActionItemId = (actionId) => {
    const match = actionId?.match(/^(.+)-act-(\d+)$/);
    if (!match) return [null, null];
    return [match[1], parseInt(match[2], 10)];
  };

  const handleStatusChange = (signalId, actionId, newStatus) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [signalId]: {
        ...prev[signalId],
        actionStatuses: {
          ...prev[signalId]?.actionStatuses,
          [actionId]: newStatus,
        },
      },
    }));
    const [alertId, index] = parseActionItemId(actionId);
    if (alertId != null && onUpdateActionItem) {
      onUpdateActionItem(alertId, index, { status: newStatus }).catch((err) => {
        console.error("Failed to save action status:", err);
      });
    }
  };

  const handleNoteChange = (signalId, actionId, note) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [signalId]: {
        ...prev[signalId],
        actionNotes: {
          ...prev[signalId]?.actionNotes,
          [actionId]: note,
        },
      },
    }));
    const [alertId, index] = parseActionItemId(actionId);
    if (alertId != null && onUpdateActionItem) {
      onUpdateActionItem(alertId, index, { note: note ?? null }).catch(
        (err) => {
          console.error("Failed to save action note:", err);
        },
      );
    }
  };

  // Sort the visible bucket: incomplete first (by impact), completed sink.
  const visible = useMemo(() => {
    // Risks by level, then opportunities.
    const impactOrder = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
      opportunity: 4,
    };
    return [...(grouped[activeStatus] ?? [])].sort((a, b) => {
      const aDone =
        a.actions.length > 0 &&
        a.actions.every((x) => x.status === "completed");
      const bDone =
        b.actions.length > 0 &&
        b.actions.every((x) => x.status === "completed");
      if (aDone !== bDone) return aDone ? 1 : -1;
      return (impactOrder[a.impact] ?? 5) - (impactOrder[b.impact] ?? 5);
    });
  }, [grouped, activeStatus]);

  const criticalCount = grouped.active.filter(
    (s) => s.impact === "critical",
  ).length;
  const highCount = grouped.active.filter((s) => s.impact === "high").length;
  const opportunityCount = grouped.active.filter(
    (s) => s.impact === "opportunity",
  ).length;

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex flex-col justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <HiSparkles className="size-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">Signals</h2>
          </div>
          <div className="flex items-center gap-3 text-xs font-semibold">
            <span className="text-emerald-600">{activeCount} Active</span>
            {criticalCount > 0 && (
              <span className="text-red-700">{criticalCount} Critical</span>
            )}
            {highCount > 0 && (
              <span className="text-red-600">{highCount} High</span>
            )}
            {opportunityCount > 0 && (
              <span className="text-emerald-600">
                {opportunityCount}{" "}
                {opportunityCount === 1 ? "Opportunity" : "Opportunities"}
              </span>
            )}
          </div>
          <HubspotSyncIndicator sync={hubspotSync} />
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5">
          {SIGNAL_STATUSES.map((status) => {
            const count = grouped[status].length;
            const selected = activeStatus === status;
            const Icon = STATUS_ICONS[status];
            return (
              <button
                key={status}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  setActiveStatus(status);
                  setExpandedId(null);
                }}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  selected
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="size-3.5" />
                {STATUS_LABELS[status]}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${
                    selected
                      ? "bg-gray-100 text-gray-600"
                      : "bg-gray-200/70 text-gray-500"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Signal list */}
      <div className="space-y-2 p-3">
        {visible.length === 0 ? (
          <EmptyState status={activeStatus} />
        ) : (
          visible.map((signal) => (
            <SignalRow
              key={signal.id}
              signal={signal}
              isExpanded={expandedId === signal.id}
              onToggle={() => handleToggle(signal.id)}
              onStatusChange={(actionId, newStatus) =>
                handleStatusChange(signal.id, actionId, newStatus)
              }
              onNoteChange={(actionId, note) =>
                handleNoteChange(signal.id, actionId, note)
              }
              onResolve={onResolve}
              onSnooze={onSnooze}
              onReject={onReject}
              onReactivate={onReactivate}
            />
          ))
        )}
      </div>
    </div>
  );
};
